import React, { useMemo, useState, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import UpcomingCourseCard from './UpcomingCourseCard';
import StandaloneAssignmentCard from './StandaloneAssignmentCard';
import { useData } from '../../contexts/DataContext';

const getCourseId = (c) => String(c?.id ?? c?.courseId ?? c?._id ?? c?.endeavorId ?? c?.endeavorID ?? c?.endeavor_id ?? '');
const getEventCourseId = (e) => String(e?.courseId ?? e?.course_id ?? e?.course ?? e?.courseID ?? e?.endeavorId ?? e?.endeavorID ?? e?.endeavor_id ?? '');
const getEventStart = (e) => e?.start ?? e?.startTime ?? e?.start_at ?? e?.startAt ?? e?.startDatetime ?? e?.startDate ?? e?.date ?? null;
const getAssignmentCourseId = (a) => String(a?.courseId ?? a?.course_id ?? a?.course ?? a?.courseID ?? a?.endeavorId ?? a?.endeavorID ?? a?.endeavor_id ?? '');
const getAssignmentDue = (a) => a?.dueAt ?? a?.due_at ?? a?.dueDate ?? a?.due ?? a?.dueDatetime ?? null;

const toMs = (v) => {
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d.getTime();
};

function uniqById(arr) {
  const m = new Map();
  (arr || []).forEach(a => {
    const id = a?.id || a?.assignmentId || a?._id || a?.assignmentID || a?._id_str || null;
    if (id == null) return;
    if (!m.has(String(id))) m.set(String(id), a);
  });
  return Array.from(m.values());
}

export default function UpcomingByCourse({ courses = [], chronologicalItems = [], courseFilter = 'all' }) {
  const ctx = useData() || {};
  const { getEndeavorEvents, getFilteredAssignments, markAssignmentCompleted, updateAssignment, dataRefreshTrigger } = ctx;

  const _now = Date.now(); // eslint-disable-line no-unused-vars

  // ── Scroll position preservation ──────────────────────────────────────
  const rowRef = useRef(null);
  const savedScrollLeft = useRef(0);

  // Save scroll position continuously so we always have the latest
  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    const handleScroll = () => { savedScrollLeft.current = el.scrollLeft; };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  // Restore scroll position after every re-render (data refresh, toggle, etc.)
  useLayoutEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    // requestAnimationFrame ensures the DOM has been painted with new children
    requestAnimationFrame(() => {
      if (el.scrollLeft !== savedScrollLeft.current) {
        el.scrollLeft = savedScrollLeft.current;
      }
    });
  });

  // ── Drag-to-scroll (grab and drag) ───────────────────────────────────
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragScrollStart = useRef(0);
  const hasDragged = useRef(false);

  const onPointerDown = useCallback((e) => {
    // Only left-click, ignore if target is interactive
    if (e.button !== 0) return;
    const tag = e.target.tagName;
    if (tag === 'BUTTON' || tag === 'INPUT' || tag === 'SELECT' || tag === 'A') return;
    // Don't start drag on checkboxes, toggle circles, or clickable card headers
    if (e.target.closest('[role="checkbox"], [role="button"], button, a, select, input')) return;

    const el = rowRef.current;
    if (!el) return;

    isDragging.current = true;
    hasDragged.current = false;
    dragStartX.current = e.clientX;
    dragScrollStart.current = el.scrollLeft;
    el.classList.add('is-grabbing');
    el.setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e) => {
    if (!isDragging.current) return;
    const dx = e.clientX - dragStartX.current;
    if (Math.abs(dx) > 3) hasDragged.current = true;
    const el = rowRef.current;
    if (el) {
      el.scrollLeft = dragScrollStart.current - dx;
    }
  }, []);

  const onPointerUp = useCallback((e) => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const el = rowRef.current;
    if (el) {
      el.classList.remove('is-grabbing');
      el.releasePointerCapture(e.pointerId);
    }
  }, []);

  // Prevent click events that resulted from a drag from firing
  const onClickCapture = useCallback((e) => {
    if (hasDragged.current) {
      e.stopPropagation();
      e.preventDefault();
      hasDragged.current = false;
    }
  }, []);

  // Local caches for canonical data per course
  const [eventsByCourse, setEventsByCourse] = useState({});
  const [assignmentsByCourse, setAssignmentsByCourse] = useState({});
  
  // Track optimistic updates
  const [optimisticUpdates, setOptimisticUpdates] = useState(new Map());
  const [animatingAssignments, setAnimatingAssignments] = useState(new Set());
  const [pendingOps, setPendingOps] = useState(new Map());

  // Fetch canonical events & assignments for visible courses
  useEffect(() => {
    let cancelled = false;
    async function fetchForCourses() {
      if (!Array.isArray(courses) || courses.length === 0) return;
      const promises = courses.map(async (course) => {
        const cidRaw = course?.id ?? course?.courseId ?? course?.endeavorId ?? course?.endeavorID ?? course?.courseID;
        const cid = cidRaw == null ? null : Number(cidRaw);
        if (cid == null) return { cid: null, events: [], assignments: [] };
        try {
          const [evs, asns] = await Promise.all([
            typeof getEndeavorEvents === 'function' ? getEndeavorEvents(cid) : Promise.resolve([]),
            typeof getFilteredAssignments === 'function' ? getFilteredAssignments({ endeavorId: cid, page: 0, size: 10000 }) : Promise.resolve([]),
          ]);
          return { cid: String(cid), events: Array.isArray(evs) ? evs : (evs?.content || []), assignments: uniqById(Array.isArray(asns) ? asns : (asns?.content || [])) };
        } catch (e) {
          return { cid: String(cid), events: [], assignments: [] };
        }
      });

      const results = await Promise.all(promises);
      if (cancelled) return;
      const evMap = {};
      const asnMap = {};
      results.forEach(r => {
        if (!r || !r.cid) return;
        evMap[r.cid] = r.events || [];
        asnMap[r.cid] = uniqById(r.assignments || []);
      });
      setEventsByCourse(evMap);
      setAssignmentsByCourse(asnMap);
    }
    fetchForCourses();
    return () => { cancelled = true; };
  }, [courses, getEndeavorEvents, getFilteredAssignments, dataRefreshTrigger]);

  // Clear optimistic updates after data refresh
  useEffect(() => {
    if (pendingOps.size === 0) return;
    const timer = setTimeout(() => {
      setAnimatingAssignments(new Set());
      setOptimisticUpdates(new Map());
      setPendingOps(new Map());
    }, 500);
    return () => clearTimeout(timer);
  }, [dataRefreshTrigger, pendingOps.size]);

  // Build course map
  const courseMap = useMemo(() => {
    const map = new Map();
    courses.forEach(c => {
      const id = getCourseId(c);
      if (id) map.set(id, c);
    });
    return map;
  }, [courses]);

  // Build a map of sorted event timestamps per course so we can find the
  // previous class session for each event.
  const sortedEventsByCourse = useMemo(() => {
    const map = {};
    chronologicalItems.forEach(item => {
      if (item.type !== 'event') return;
      const courseId = getEventCourseId(item.data);
      if (!courseId) return;
      if (!map[courseId]) map[courseId] = [];
      const ts = toMs(getEventStart(item.data));
      if (ts) map[courseId].push(ts);
    });
    // Also include past events from eventsByCourse so we can look back
    // beyond the current time window
    Object.entries(eventsByCourse).forEach(([courseId, evts]) => {
      if (!map[courseId]) map[courseId] = [];
      (evts || []).forEach(evt => {
        const ts = toMs(getEventStart(evt));
        if (ts) map[courseId].push(ts);
      });
    });
    // Deduplicate and sort ascending
    Object.keys(map).forEach(courseId => {
      map[courseId] = [...new Set(map[courseId])].sort((a, b) => a - b);
    });
    return map;
  }, [chronologicalItems, eventsByCourse]);

  // Build renderItems from chronologicalItems
  const renderItems = useMemo(() => {
    const items = [];

    chronologicalItems.forEach(item => {
      if (item.type === 'event') {
        const evt = item.data;
        const courseId = getEventCourseId(evt);
        const course = courseMap.get(courseId);
        if (!course) return;

        // Get assignments for this event
        const courseAssignments = (assignmentsByCourse[courseId] || []).map(a => {
          const aid = a.id || a.assignmentId || a._id;
          const optimistic = optimisticUpdates.get(String(aid));
          if (optimistic) {
            return { ...a, ...optimistic, __dueMs: toMs(getAssignmentDue(a)) };
          }
          return { ...a, __dueMs: toMs(getAssignmentDue(a)) };
        }).filter(a => a.__dueMs != null);

        const eventStart = toMs(getEventStart(evt));

        // Find the previous class session for this course to determine
        // the window of assignments to show on this card.
        // We want assignments due AFTER the previous session and up to
        // (inclusive) the end of this event's day.
        const courseEvents = sortedEventsByCourse[courseId] || [];
        let prevSessionEnd = 0; // default: beginning of time
        for (let i = 0; i < courseEvents.length; i++) {
          if (courseEvents[i] >= eventStart) break;
          prevSessionEnd = courseEvents[i];
        }

        // End of the current event's calendar day (23:59:59.999)
        const eventDayEnd = eventStart
          ? new Date(new Date(eventStart).setHours(23, 59, 59, 999)).getTime()
          : null;

        // Filter assignments due between the previous class session
        // (exclusive) and this class session's day (inclusive)
        const dueOnEventDay = courseAssignments.filter(a => {
          if (!eventDayEnd) return false;
          const dueMs = a.__dueMs;
          if (!dueMs) return false;
          return dueMs > prevSessionEnd && dueMs <= eventDayEnd;
        });

        // Sort assignments
        dueOnEventDay.sort((a, b) => {
          const aId = a.id || a.assignmentId || a._id;
          const bId = b.id || b.assignmentId || b._id;
          const aAnimating = animatingAssignments.has(String(aId));
          const bAnimating = animatingAssignments.has(String(bId));
          if (aAnimating || bAnimating) return 0;
          const aCompleted = !!(a.completed || a.isDone || a.isCompleted || a.status === 'Completed');
          const bCompleted = !!(b.completed || b.isDone || b.isCompleted || b.status === 'Completed');
          if (aCompleted !== bCompleted) return aCompleted ? 1 : -1;
          return a.__dueMs - b.__dueMs;
        });

        items.push({
          type: 'event',
          course,
          event: evt,
          assignments: dueOnEventDay,
          _courseId: courseId,
          _eventStartMs: eventStart
        });
      }
      // standaloneAssignment items are no longer rendered as separate cards —
      // they are absorbed into event cards below.
    });

    // ── Roll standalone assignments into the nearest NEXT event card ──
    // For each standalone assignment, find the first event card for the same
    // course whose event starts on or after the assignment due date.  If none
    // exists (the assignment is due after all visible events), attach to the
    // LAST event card for that course.
    chronologicalItems.forEach(item => {
      if (item.type !== 'standaloneAssignment') return;
      const asg = item.data;
      const courseId = getAssignmentCourseId(asg);
      const dueMs = item.timestamp;
      const aid = String(asg.id || asg.assignmentId || asg._id || '');

      // Find event cards for this course
      const courseEvents = items.filter(i => i.type === 'event' && i._courseId === courseId);
      if (courseEvents.length === 0) return; // no event cards → nothing to attach to

      // Prefer the first event card starting on or after the due date
      let target = courseEvents.find(e => e._eventStartMs >= dueMs);
      // Fallback: if due after all events, attach to the last event card
      if (!target) target = courseEvents[courseEvents.length - 1];

      // Avoid duplicates — the event card may already have this assignment
      // from the assignmentsByCourse canonical fetch
      const alreadyPresent = target.assignments.some(a => {
        const existingId = String(a.id || a.assignmentId || a._id || '');
        return existingId && existingId === aid;
      });
      if (alreadyPresent) return;

      // Apply optimistic updates if any
      const optimistic = optimisticUpdates.get(aid);
      const enriched = optimistic
        ? { ...asg, ...optimistic, __dueMs: dueMs }
        : { ...asg, __dueMs: dueMs };
      target.assignments.push(enriched);

      // Re-sort after insertion
      target.assignments.sort((a, b) => {
        const aId2 = a.id || a.assignmentId || a._id;
        const bId2 = b.id || b.assignmentId || b._id;
        const aAnimating2 = animatingAssignments.has(String(aId2));
        const bAnimating2 = animatingAssignments.has(String(bId2));
        if (aAnimating2 || bAnimating2) return 0;
        const aCompleted2 = !!(a.completed || a.isDone || a.isCompleted || a.status === 'Completed');
        const bCompleted2 = !!(b.completed || b.isDone || b.isCompleted || b.status === 'Completed');
        if (aCompleted2 !== bCompleted2) return aCompleted2 ? 1 : -1;
        return (a.__dueMs || 0) - (b.__dueMs || 0);
      });
    });

    return items;
  }, [chronologicalItems, courseMap, assignmentsByCourse, optimisticUpdates, animatingAssignments, sortedEventsByCourse]);

  const toggleDone = useCallback(async (assignment) => {
    const id = assignment?.id ?? assignment?.assignmentId ?? assignment?._id;
    if (!id) {
      console.warn('UpcomingByCourse: No assignment ID found', assignment);
      return;
    }

    const isCurrentlyCompleted = !!(assignment?.completed || assignment?.isDone || assignment?.isCompleted || assignment?.status === 'Completed');
    const idStr = String(id);
    
    const optimisticState = {
      completed: !isCurrentlyCompleted,
      isDone: !isCurrentlyCompleted,
      status: !isCurrentlyCompleted ? 'Completed' : 'active'
    };
    
    setOptimisticUpdates(prev => {
      const next = new Map(prev);
      next.set(idStr, optimisticState);
      return next;
    });
    
    setAnimatingAssignments(prev => new Set(prev).add(idStr));

    // After a brief delay, clear the animation guard so the sort kicks in
    // and completed assignments move to the bottom of the list
    setTimeout(() => {
      setAnimatingAssignments(prev => {
        const next = new Set(prev);
        next.delete(idStr);
        return next;
      });
    }, 500);

    try {
      if (!isCurrentlyCompleted) {
        if (typeof markAssignmentCompleted === 'function') {
          await markAssignmentCompleted(id);
        } else if (typeof updateAssignment === 'function') {
          await updateAssignment(id, { completed: true, status: 'Completed' });
        }
      } else {
        if (typeof updateAssignment === 'function') {
          const dueRaw = assignment.dueAt || assignment.due_at || assignment.dueDate || assignment.dueDatetime || assignment.due;
          const payload = {
            assignmentId: id,
            title: assignment.title || assignment.name || '',
            description: assignment.description || null,
            assignedDate: assignment.assignedDate || assignment.assigned_date || null,
            dueDatetime: dueRaw,
            endeavorId: assignment.endeavorId || assignment.endeavorID || assignment.endeavor_id || null,
            eventId: assignment.eventId || assignment.eventID || assignment.event_id || null,
            priority: assignment.priority || null,
            completed: false,
            status: 'active',
            completedDatetime: null
          };
          await updateAssignment(id, payload);
        }
      }
      
      setPendingOps(prev => {
        const next = new Map(prev);
        next.set(idStr, Date.now());
        return next;
      });
    } catch (e) {
      console.error('UpcomingByCourse: Assignment toggle failed', e);
      setOptimisticUpdates(prev => {
        const next = new Map(prev);
        next.delete(idStr);
        return next;
      });
      setAnimatingAssignments(prev => {
        const next = new Set(prev);
        next.delete(idStr);
        return next;
      });
    }
  }, [markAssignmentCompleted, updateAssignment]);

  const hasCourses = Array.isArray(courses) && courses.length > 0;

  if (renderItems.length === 0 && !hasCourses) {
    return (
      <div className="upcomingByCourseRow" ref={rowRef}>
        <div className="study-timeline-empty">
          <div className="study-timeline-empty-glow" />

          {/* Icon ring */}
          <div className="study-timeline-empty-icon-ring">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
          </div>

          <h3 className="study-timeline-empty-title">Your study timeline will appear here</h3>
          <p className="study-timeline-empty-desc">
            Once you add courses, we'll organize upcoming classes, assignments, and key dates into one study timeline.
          </p>

          {/* Feature pills */}
          <div className="study-timeline-empty-pills">
            <span className="study-timeline-pill">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              See what's due next
            </span>
            <span className="study-timeline-pill">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
              Track classes &amp; assignments
            </span>
            <span className="study-timeline-pill">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
              Plan your study week
            </span>
          </div>

          {/* Ghost timeline preview */}
          <div className="study-timeline-empty-ghosts">
            <div className="study-timeline-ghost-row">
              <div className="study-timeline-ghost-dot" />
              <div className="study-timeline-ghost-line" />
              <div className="study-timeline-ghost-badge" />
            </div>
            <div className="study-timeline-ghost-row">
              <div className="study-timeline-ghost-dot" />
              <div className="study-timeline-ghost-line" style={{ width: '60%' }} />
              <div className="study-timeline-ghost-badge" />
            </div>
            <div className="study-timeline-ghost-row">
              <div className="study-timeline-ghost-dot" />
              <div className="study-timeline-ghost-line" style={{ width: '45%' }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (renderItems.length === 0 && hasCourses) {
    return (
      <div className="upcomingByCourseRow" ref={rowRef}>
        <div className="study-timeline-empty study-timeline-empty--has-courses">
          <div className="study-timeline-empty-icon-ring">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <h3 className="study-timeline-empty-title">You're all caught up</h3>
          <p className="study-timeline-empty-desc">
            No upcoming classes or assignments in this time window. Try expanding the range above to see more.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="upcomingByCourseRow"
      ref={rowRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onClickCapture={onClickCapture}
    >
      {renderItems.map((item, index) => {
        if (item.type === 'event') {
          return (
            <UpcomingCourseCard
              key={`event-${item.event?.id || item.event?.eventId || index}`}
              course={item.course}
              nextEvent={item.event}
              event={item.event}
              dueAssignments={item.assignments}
              onToggleAssignment={(a) => toggleDone(a)}
            />
          );
        }
        return null;
      })}
    </div>
  );
}